<template>
  <section :class="[$style.product_list, PRODUCT_LIST_CLASS]">
    <div :class="$style.product_list_container">
      <ProductFilterV2
        :is-open-filter-sp="isOpenFilterSp"
        :sort="sort"
        :all-count="previousSearchResult.allCount"
        :all-list-categories="allListCategories"
        :filter="filter"
        :filter-counts="filterCounts ?? {}"
        :is-link-category="false"
        @update:sort="$emit('update:sort', $event)"
        @update:filter="$emit('update:filter', $event)"
        @reset="$emit('reset')"
        @update:is-open-filter-sp="isOpenFilterSp = $event"
      />
      <div :class="$style.main_contents">
        <div :class="$style.search_area">
          <form :class="$style.search_form" action="#" autocomplete="off" @submit.prevent="keywordSearch">
            <AtomsConsumerInput
              ref="searchRef"
              v-model="keyword"
              type="search"
              placeholder="検索"
              :class="$style.search_input"
            />
            <AtomsConsumerImage src="/images/icon/primary-search.svg" :class="$style.search_icon" alt="検索" />
          </form>
        </div>

        <div :class="$style.products_box">
          <div :class="$style.sort_wrapper">
            <div>
              <p v-if="previousSearchResult.keyword" :class="$style.items_count">
                {{ previousSearchResult.keyword }} で検索
              </p>

              <p :class="$style.items_count">
                全 {{ previousSearchResult.allCount }} 件中
                {{ `${previousSearchResult.pageStart}〜${previousSearchResult.pageEnd}` }} 件を表示
              </p>
            </div>
            <div v-if="filter.sort" :class="$style.products_order">
              <AtomsConsumerButton :class="$style.order_button" @click="isSelectOrderOpen = !isSelectOrderOpen">{{
                ProductAllSearchInput_SortItems[filter.sort]
              }}</AtomsConsumerButton>
              <div v-show="isSelectOrderOpen" :class="$style.order_accordion">
                <AtomsConsumerRadioButton
                  v-for="[sortKey, sortLabel] in Object.entries(ProductAllSearchInput_SortItems)"
                  :key="sortKey"
                  :checked="filter.sort"
                  name="sort"
                  :value="sortKey"
                  @change="(v) => $emit('update:sort', v)"
                  @click="isSelectOrderOpen = false"
                >
                  {{ sortLabel }}
                </AtomsConsumerRadioButton>
              </div>
              <div v-if="isSelectOrderOpen" :class="$style.overlay" @click="isSelectOrderOpen = !isSelectOrderOpen" />
            </div>
          </div>
          <ProductQuickFilterV2
            v-if="!md"
            :all-list-categories="allListCategories"
            :filter="filter"
            :filter-counts="filterCounts ?? {}"
            :is-link-category="false"
            @update:filter="$emit('update:filter', $event)"
            @update:open-filter="isOpenFilterSp = $event"
          />
          <div :class="$style.list_wrap">
            <ClientOnly>
              <PagePartsProductListItemV2
                v-for="(product, index) in products"
                :key="product.document_id"
                :product="product"
                :search-id="''"
                :current-page="pager.currentPage"
                :position="index + 1"
                :enable-ga-tracking="true"
                @open-fav-modal="handleOpenFavModal($event, product)"
              />
            </ClientOnly>
          </div>
          <div :class="$style.pager_wrap">
            <MoleculesConsumerPager
              :total-page="pager.totalPage"
              :current-page="pager.currentPage"
              :update-page-mode="'emit'"
              @update-page="$emit('updatePage', $event)"
            />
          </div>
        </div>
      </div>
    </div>
    <MoleculesConsumerModal v-model="isOpenFavModal">
      <ProductSelectFav
        :product="favModalProduct"
        :favorite-list="favoriteListRef"
        :is-processing="isProcessing"
        @on-favorite="onFavoriteByProductList"
        @lift="isOpenFavModal = false"
      />
    </MoleculesConsumerModal>
  </section>
</template>

<script setup lang="ts">
import { ProductAllSearchInput_Sort, ProductAllSearchInput_SortItems, PRODUCT_LIST_CLASS } from "~/const/product"
import type { ProductAllListProps } from "~/types/product"
import { useFavorite } from "~/composables/useFavorite"
import { usePublicSkuFindManyQuery } from "~/gql/urql.generated"
import type { PublicSkuFindManyQueryVariables } from "~/types/type.generated"
import { useToast } from "~/state"

const props = defineProps<ProductAllListProps>()

const keyword = ref(props.previousSearchResult.keyword)
const isOpenFilterSp = ref(false)
const { openFavModal, onFavoriteByProductList, isOpenFavModal, favModalProduct, favoriteListRef, isProcessing } =
  useFavorite()

const emit = defineEmits<{
  (e: "update:sort", value: ProductAllSearchInput_Sort): void
  (e: "update:filter", value: ProductAllListProps["filter"]): void
  (e: "keywordSearch", value: string): void
  (e: "updatePage", value: number): void
  (e: "reset"): void
}>()

const searchElement = ref<HTMLInputElement>()
const isSelectOrderOpen = ref<boolean>(false)

const { width: windowWidth } = useWindowSize()
const md = computed(() => windowWidth.value >= 768)

const keywordSearch = () => {
  emit("keywordSearch", keyword.value)
  // SP向け: 検索後にキーボードを非表示 -> 検索結果が見れるように
  searchElement.value?.blur()
}

const toast = useToast()
const publicSkuFindManyQueryVariables = ref<PublicSkuFindManyQueryVariables>({})
const { executeQuery: publicSkuFindManyQueryExec, resume: publicSkuFindManyQueryResume } = usePublicSkuFindManyQuery({
  requestPolicy: "network-only",
  variables: publicSkuFindManyQueryVariables,
  pause: true,
})

const handleOpenFavModal = async (e: Event, product: (typeof props.products)[number]) => {
  e.preventDefault()

  publicSkuFindManyQueryVariables.value = {
    filter: {
      product_id: product.document_id,
      is_status: true,
    },
  }
  await publicSkuFindManyQueryResume()
  // TODO: ローディングマスク表示させたい
  const { data, error } = await publicSkuFindManyQueryExec()
  if (!data.value?.publicSkuFindMany.length || error.value) {
    toast?.showErrorToasted("お気に入り情報の取得に失敗しました。再度お試しください。")
    return
  }

  openFavModal(e, {
    document_id: product.document_id,
    name: product.name,
    material: product.material,
    skus: data.value.publicSkuFindMany
      .sort((a, b) => a.display_order_number - b.display_order_number)
      .map((sku) => ({
        document_id: sku.document_id,
        color: sku.color,
        size: sku.size,
        price: sku.price,
        main_image: sku.main_image,
      })),
  })
}

watch(
  () => props.previousSearchResult.keyword,
  () => {
    keyword.value = props.previousSearchResult.keyword
  },
)
</script>

<style lang="scss" scoped module>
.product_list {
  .product_list_container {
    display: flex;
    justify-content: center;
    padding: 0;
    @include md {
      padding: 0 1rem;
    }
    .main_contents {
      flex-grow: 1;
      width: 100%;
      max-width: $max-width-md;

      .search_area {
        margin-bottom: 1rem;
        padding: 0 1rem;
        @include md {
          padding: 0;
        }
        .search_form {
          position: relative;
          width: 100%;
          .search_input {
            padding-left: 2.5rem;
          }
          .search_icon {
            position: absolute;
            top: 50%;
            left: 1rem;
            width: 1rem;
            height: 1rem;
            transform: translateY(-50%);
          }
        }
      }

      .products_box {
        .sort_wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 0.5rem;
          background-color: $white;
          border-top: 1px solid $primary-20;
          border-bottom: 1px solid $primary-20;

          @include md {
            border: none;
          }

          .items_count {
            color: $text-gray;
            line-height: 1.25rem;
            font-size: $font-size-14;
          }

          .products_order {
            position: relative;
            font-size: $font-size-14;
            font-weight: bold;
            min-width: 6.5rem;

            .order_button {
              @include sub-font;
              position: relative;
              padding: 0 0 0 1.5rem;
              font-size: $font-size-13;
              color: $black;
              cursor: pointer;
              text-decoration: underline;

              &:hover {
                opacity: 0.7;
              }

              &:after {
                position: absolute;
                top: 50%;
                left: 0;
                width: 14px;
                height: 16px;
                content: "";
                background: url("/images/common/arrow/sort.svg");
                background-size: contain;
                transform: translateY(-50%);
              }
            }

            .order_accordion {
              position: absolute;
              top: 2rem;
              right: -0.5rem;
              z-index: 10;
              width: 100vw;
              padding: 1rem;
              background-color: $white;
              filter: drop-shadow(0 0.25rem 0.25rem rgba($black, 0.1));
              @include md {
                right: 0;
                width: 20rem;
              }
              :global {
                label {
                  padding: 0.5rem;
                  border-bottom: 1px solid $primary-20;
                  flex-direction: row-reverse;
                  justify-content: space-between;
                  &:last-child {
                    border-bottom: none;
                  }
                  input {
                    border: none;
                    &::after,
                    &::before:not(:checked) {
                      display: none;
                    }
                    // チェックアイコン
                    &:checked {
                      font-weight: bold;
                      &::before {
                        top: calc(50% - 0.375rem);
                        right: 0;
                        left: auto;
                        transform: rotate(50deg);
                        width: 0.45rem;
                        height: 0.75rem;
                        border-radius: 0;
                        background: none;
                        border-right: 2px solid $secondary;
                        border-bottom: 2px solid $secondary;
                        content: "";
                      }
                    }
                  }
                }
              }
            }

            .overlay {
              position: fixed;
              inset: 0px;
            }
          }
        }
        .list_wrap {
          display: grid;
          grid-template-columns: repeat(auto-fit, calc(100% / 2 - (0.5rem / 2)));
          column-gap: 0.5rem;
          row-gap: 1rem;
          padding: 0 0.5rem;
          margin: 1rem 0;
          @include md {
            grid-template-columns: repeat(auto-fit, calc(100% / 3 - (1rem / 3 * 2)));
            column-gap: 1rem;
            row-gap: 1.5rem;
            margin-bottom: 2rem 0 5rem;
          }
        }
      }
    }

    .pager_wrap {
      display: flex;
      justify-content: flex-end;
      margin-right: 1rem;
    }
  }
}

.error_message {
  margin-top: 0.5rem;
  font-size: 12px;
  color: $danger;
}
</style>
